import { OrderDto } from '../../types/opsBriefing';

/**
 * 강사 앱 미리보기에 쓸 지시 뽑기.
 *
 * ─────────────────────────────────────────────────────────────
 * 미리보기는 **실제로 나간 지시**에서 나와야 합니다.
 * ─────────────────────────────────────────────────────────────
 * 예전에는 목 지시(mockCoachTasks)로 초기화했습니다. 그러면 원장님이
 * "강사에게 어떻게 보이는지" 확인하려고 열었을 때 우리 센터에 없는
 * 회원 이름과 없는 센터 이름이 뜹니다. 확인이 아니라 오해가 됩니다.
 *
 * 어느 강사를 보여줄지
 *   아직 실행되지 않은 지시가 가장 많은 강사를 고릅니다.
 *   원장이 확인하고 싶은 화면은 "할 일이 쌓여 있는 강사가 보는 화면"입니다.
 *   동률이면 지시 총 건수가 많은 쪽. 그래도 같으면 목록 순서(최신 발송 순)를 따릅니다.
 *
 * 지시가 하나도 없으면 null 입니다 — 미리보기를 지어내지 않습니다.
 */

export type CoachPreview = {
    coachId: string;
    coachName: string;
    /** 아직 안 끝난 건이 위로 */
    tasks: OrderDto[];
};

const isOpen = (o: OrderDto) => o.status !== 'DONE';

export const pickCoachPreview = (orders: OrderDto[]): CoachPreview | null => {
    /*
     * 원장이 이미 정리한(ARCHIVED) 건은 강사 앱에도 남지 않는다.
     *
     * 철회한(RETRACTED) 건도 뺀다. 이 미리보기는 "강사가 지금 뭘 보고 있나"를
     * 원장에게 보여주는 자리인데, 철회한 지시가 여기서 **아직 안 한 일**로
     * 세어지면(isOpen 은 DONE 이 아닌 것을 전부 열린 것으로 본다) 원장은
     * 자기가 취소한 일을 강사가 안 하고 있다고 읽는다.
     */
    const live = orders.filter((o) => o.status !== 'ARCHIVED' && o.status !== 'RETRACTED');
    if (live.length === 0) return null;

    const groups: { key: string; list: OrderDto[] }[] = [];
    live.forEach((o) => {
        // coachId 가 비어 오는 과도기 응답도 있어 이름으로 한 번 더 받는다
        const key = o.coachId || o.coachName;
        const found = groups.find((g) => g.key === key);
        if (found) found.list.push(o);
        else groups.push({ key, list: [o] });
    });

    const openCount = (list: OrderDto[]) => list.filter(isOpen).length;
    const picked = [...groups].sort((a, b) => {
        const diff = openCount(b.list) - openCount(a.list);
        if (diff !== 0) return diff;
        return b.list.length - a.list.length;
    })[0];

    const tasks = [...picked.list].sort((a, b) => Number(isOpen(b)) - Number(isOpen(a)));
    const head = tasks[0];

    return { coachId: head.coachId, coachName: head.coachName, tasks };
};
